import { useSearchParams } from 'react-router-dom'
import { useCallback } from 'react'

/**
 * Generic hook for managing state in URL search params
 * Updates URL without creating new history entries (replace: true)
 */
export function useUrlState<T extends Record<string, any>>(
  defaultValues: T,
  serializers: {
    [K in keyof T]?: {
      serialize: (value: T[K]) => string | undefined
      deserialize: (value: string | null) => T[K]
    }
  } = {}
) {
  const [searchParams, setSearchParams] = useSearchParams()

  // Deserialize current state from URL
  const getStateFromUrl = useCallback((): T => {
    const state = { ...defaultValues }

    for (const key in defaultValues) {
      const paramValue = searchParams.get(key)
      const serializer = serializers[key]

      if (serializer && serializer.deserialize) {
        state[key] = serializer.deserialize(paramValue)
      } else if (paramValue !== null) {
        // Default deserialization
        state[key] = paramValue as T[typeof key]
      }
    }

    return state
  }, [searchParams, defaultValues, serializers])

  // Update URL with new state
  const updateUrlState = useCallback(
    (updates: Partial<T>) => {
      const newParams = new URLSearchParams(searchParams)

      for (const key in updates) {
        const value = updates[key]
        const serializer = serializers[key]

        let serializedValue: string | undefined

        if (serializer && serializer.serialize) {
          serializedValue = serializer.serialize(value)
        } else if (value !== undefined && value !== null && value !== '') {
          // Default serialization
          serializedValue = String(value)
        }

        if (serializedValue !== undefined && serializedValue !== '') {
          newParams.set(key, serializedValue)
        } else {
          newParams.delete(key)
        }
      }

      // Use replace: true to avoid creating history entries for filter changes
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams, serializers]
  )

  return {
    state: getStateFromUrl(),
    updateState: updateUrlState,
  }
}

